import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation, useParams } from 'react-router-dom';

import CountyRankings from '../components/content/golfcourses/golfrankings/CountyRanking';
import CountyGolfCoursesHeader from '../components/content/golfcourses/headers/CountyGolfCoursesHeader';
import NotFound from '../components/error/NotFound';
import CountyCourseMap from '../components/maps/CountyCourseMap';
import JsonLd from '../components/meta/JsonLd';
import MetaTags from '../components/meta/MetaTags';
import { setSelectedCountySlug } from '../features/selectedCounty/selectedCountySlice';
import { slugify } from '../utils/slugify';
import { supabase } from '../utils/supabase';

function CountyPage() {
  const dispatch = useDispatch();
  const location = useLocation();
  const { countySlug } = useParams();
  const selectedCountySlug = useSelector(
    (state) => state.selectedCounty?.countySlug ?? countySlug,
  );
  const [selectedCountryX, setSelectedCountryX] = useState(null);
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const countyURL = `https://api.golflists.com/storage/v1/object/public/county-flags-graph/${countySlug}.webp`;
  const countyFlagURL = `https://api.golflists.com/storage/v1/object/public/county-flags-graph/${countySlug}.webp`;

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.prerenderReady = false;
    }
  }, [countySlug]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const { data: countyData, error: countyError } = await supabase
          .from('counties')
          .select('county_name, country_of_county, slug_county_name')
          .eq('slug_county_name', countySlug)
          .single();

        if (countyError || !countyData) {
          setError('Could not fetch county');
          setIsLoading(false);
          return;
        }

        const properCountyName = countyData.county_name;
        setSelectedCounty(properCountyName);
        setSelectedCountryX(countyData.country_of_county);
        dispatch(setSelectedCountySlug(countyData.slug_county_name));

        const { data: golfCoursesData, error: coursesError } = await supabase
          .from('golf_courses')
          .select('*')
          .eq('course_county', properCountyName)
          .order('county_rank', { ascending: true });

        if (coursesError) {
          setError('Could not fetch golf courses');
          setCourses([]);
        } else {
          setCourses(golfCoursesData ?? []);
        }
      } catch (err) {
        console.error('County page fetch error:', err);
        setError('Something went wrong');
        setCourses([]);
      } finally {
        setIsLoading(false);
        if (typeof window !== 'undefined') {
          window.prerenderReady = true;
        }
      }
    };

    fetchData();
  }, [countySlug, dispatch]);

  const handleCountyChange = async (newCountySlug) => {
    dispatch(setSelectedCountySlug(newCountySlug));
    window.location.replace(
      `/lists/golf-courses/county-ranking/${newCountySlug}`,
    );
  };

  const currentYear = new Date().getFullYear();
  const metaTitle = selectedCounty
    ? `Best Golf Courses in ${selectedCounty}`
    : 'County Golf Course Rankings | GolfLists';
  const metaDescription = selectedCounty
    ? `Best Golf Courses in ${selectedCounty} including county golf course rankings for ${selectedCounty}, course information, course style and green fee data.`
    : 'Discover ranked golf courses by county. Course information, styles and green fee data for UK and Ireland.';
  const metaKeywords = selectedCounty
    ? `Golf Courses, Golf ${selectedCounty}, golf near me, golf in ${selectedCounty}, green fees, architect, designer, style, links, parkland, heathland, top 100`
    : 'Golf Courses, golf rankings, county rankings, green fees, architect, designer, links, parkland, heathland, top 100';

  const itemListSchema =
    selectedCounty && courses.length > 0
      ? {
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          name: `Best Golf Courses in ${selectedCounty}`,
          description: `Ranked list of golf courses in ${selectedCounty}, UK and Ireland.`,
          numberOfItems: courses.length,
          itemListElement: courses
            .slice(0, 50)
            .filter((c) => c.course_name)
            .map((course, index) => ({
              '@type': 'ListItem',
              position: index + 1,
              item: {
                '@type': 'GolfCourse',
                name: course.course_name,
                url: `https://golflists.com/lists/golf-courses/${slugify(course.course_name)}`,
              },
            })),
        }
      : null;

  const topThreeCourses = courses
    .filter((c) => c.county_rank != null)
    .sort((a, b) => (a.county_rank ?? 999) - (b.county_rank ?? 999))
    .slice(0, 3);

  if (isLoading && !selectedCounty) {
    return (
      <>
        <MetaTags
          title={metaTitle}
          description={metaDescription}
          imageUrl={countyURL}
          url={`https://golflists.com${location.pathname}`}
          keywords={metaKeywords}
        />
        <div className="mx-auto w-full bg-gray-100 py-2 flex items-center justify-center min-h-[400px]">
          <div className="animate-pulse text-gray-500">Loading rankings...</div>
        </div>
      </>
    );
  }

  if (error && !selectedCounty) {
    return (
      <>
        <MetaTags
          title="County Not Found | GolfLists"
          description="This county ranking page could not be found on GolfLists."
          url={`https://golflists.com${location.pathname}`}
          noindex
          statusCode={404}
        />
        <NotFound />
      </>
    );
  }

  return (
    <>
      <MetaTags
        title={metaTitle}
        description={metaDescription}
        imageUrl={countyURL}
        url={`https://golflists.com${location.pathname}`}
        keywords={metaKeywords}
      />
      {itemListSchema && <JsonLd data={itemListSchema} />}
      <div className="mx-auto w-full bg-gray-100 py-2">
        <div className="mx-auto bg-white mb-2 w-11/12 shadow-lg rounded-lg p-6 flex flex-row flex-wrap md:flex-nowrap justify-between items-center text-center">
          <img
            src={countyFlagURL}
            alt={`${selectedCounty} County Flag`}
            className="hidden sm:block mx-auto shadow-lg rounded-lg w-[100px] xl:w-[175px] h-auto align-middle border-none"
            loading="lazy"
            width="175"
            height="117"
          />
          <div>
            <h1 className="text-2xl font-bold mb-2">
              {`Best Golf Courses in ${selectedCounty} in ${currentYear}`}
            </h1>
            <h2 className="text-lg mb-3">
              {`${selectedCounty} County Golf Course Rankings and Map`}
            </h2>
            {topThreeCourses.length > 0 && (
              <div className="text-sm text-slate-600 max-w-2xl mx-auto mb-3 space-y-2">
                <p className="font-medium text-slate-700">
                  Top 3 in {selectedCounty}:
                </p>
                <ol className="list-decimal list-inside space-y-0.5 marker:font-semibold marker:text-indigo-600">
                  {topThreeCourses.map((c) => (
                    <li key={c.course_uid || c.course_name}>
                      <Link
                        to={`/lists/golf-courses/${slugify(c.course_name)}`}
                        className="font-medium text-indigo-600 hover:text-indigo-800 hover:underline"
                      >
                        {c.course_name}
                      </Link>
                      {c.county_rank != null && (
                        <span className="text-slate-500 ml-1">
                          (#{c.county_rank})
                        </span>
                      )}
                    </li>
                  ))}
                </ol>
                <p>
                  GolfLists ranks {courses.length} courses in {selectedCounty} —
                  from championship links to parkland and heathland. Explore the
                  full ranking and map below.
                </p>
              </div>
            )}
            <p className="text-sm text-slate-600">
              {`Discover pay to play, public, member friendly and private golf courses in ${selectedCounty}.`}
            </p>
          </div>
          <img
            src={countyFlagURL}
            alt={`${selectedCounty} County Flag`}
            className="mx-auto shadow-lg rounded-lg w-[100px] xl:w-[175px] h-auto align-middle border-none"
            loading="lazy"
            width="175"
            height="117"
          />
        </div>

        <div className="relative w-11/12 mx-auto max-h-[500px]">
          <CountyCourseMap key={selectedCounty} courses={courses} />
        </div>
      </div>

      <CountyGolfCoursesHeader
        onChange={handleCountyChange}
        value={selectedCounty}
        selectedCountySlug={selectedCountySlug}
        selectedCounty={selectedCounty}
      />
      <CountyRankings
        selectedCountySlug={selectedCountySlug}
        selectedCountryX={selectedCountryX}
        selectedCounty={selectedCounty}
        golfCourses={courses}
      />
    </>
  );
}

export default CountyPage;
